'use client';

import { ReactNode } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';

import { getWebReactQueryClient, reactQueryClient } from '../reactQueryClient';

import { ReactQueryDevtools } from './ReactQueryDevTools';

interface IReactQueryProviderProps {
  /**
   * The children elements to consume the provider content.
   */
  children: ReactNode;
  /**
   * Since DevTools with react-query are rendered as HTML dom elements for the
   * web, we need to disable this feature for native.
   *
   * Read more here - https://tanstack.com/query/v4/docs/react/react-native
   */
  hasDevTools?: boolean;
}

/**
 * -----------------------------------------------------------------------------
 * This wraps around the parts of the application that should consume get access
 * to the query client and cache.
 */
export function ReactQueryProvider({
  children,
  hasDevTools = false,
}: IReactQueryProviderProps) {
  return (
    <QueryClientProvider client={reactQueryClient}>
      {children}
      {hasDevTools ? <ReactQueryDevtools /> : null}
    </QueryClientProvider>
  );
}

/**
 * -----------------------------------------------------------------------------
 * This wraps around the parts of the application that should consume get access
 * to the query client and cache.
 */
export function ReactQueryProviderNextApp({
  children,
  hasDevTools = true,
}: IReactQueryProviderProps) {
  // NOTE: Avoiding useState when initializing the query client if we don't
  //       have a suspense boundary between this and the code that may
  //       suspend because React will throw away the client on the initial
  //       render if it suspends and there is no boundary
  const queryClient = getWebReactQueryClient();

  return (
    <QueryClientProvider client={queryClient}>
      {children}
      {hasDevTools ? <ReactQueryDevtools /> : null}
    </QueryClientProvider>
  );
}
